import { useLayoutEffect, useRef } from 'react';

import { di } from 'react-magnetic-di';

import type { initAnalyticsClient } from '@atlassian/atlassian-studio-observability';
import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

import type { WorkspaceContext } from './index';

type ResolutionSource = NonNullable<WorkspaceContext['resolutionSource']> | 'error';
const sources = ['localStorage', 'worklens', 'singleWorkspace', 'picker', 'noWorkspace', 'error'];
const buckets = ['unknown', '0', '1', '2-5', '6+'];
export const WORKSPACE_RESOLUTION_ANALYTICS_STORAGE_KEY =
	'studio-foundations.workspace-resolution-analytics';

const getWorkspaceCountBucket = (count?: number): string => {
	if (count === undefined) {
		return 'unknown';
	}
	if (count <= 1) {
		return String(count);
	}
	return count <= 5 ? '2-5' : '6+';
};

interface QueuedResolution {
	atlassianAccountId: string;
	source: ResolutionSource;
	workspaceCountBucket: string;
}

let pendingWrite: Promise<void> = Promise.resolve();

export const trackRootWorkspaceResolution = (
	atlassianAccountId: string,
	source: ResolutionSource,
	workspaceCount?: number,
): void => {
	const resolution = {
		atlassianAccountId,
		source,
		workspaceCountBucket: getWorkspaceCountBucket(workspaceCount),
	};
	pendingWrite = pendingWrite.then(async () => {
		try {
			await AtlBrowserStorageSession.setItem(
				WORKSPACE_RESOLUTION_ANALYTICS_STORAGE_KEY,
				JSON.stringify(resolution),
			);
		} catch {}
	});
};

const readQueuedResolution = (): QueuedResolution | undefined => {
	const stored = AtlBrowserStorageSession.getItem(WORKSPACE_RESOLUTION_ANALYTICS_STORAGE_KEY);
	if (!stored) {
		return undefined;
	}
	let value: unknown;
	try {
		value = JSON.parse(stored);
	} catch {
		return undefined;
	}
	if (typeof value !== 'object' || value === null) {
		return undefined;
	}
	const { atlassianAccountId, source, workspaceCountBucket } = value as Record<string, unknown>;
	if (
		typeof atlassianAccountId === 'string' &&
		typeof source === 'string' &&
		sources.includes(source) &&
		typeof workspaceCountBucket === 'string' &&
		buckets.includes(workspaceCountBucket)
	) {
		return { atlassianAccountId, source: source as ResolutionSource, workspaceCountBucket };
	}
	return undefined;
};

export const sendTrackedRootWorkspaceResolution = async ({
	analyticsClient,
	atlassianAccountId,
}: {
	analyticsClient: Pick<
		ReturnType<typeof initAnalyticsClient>,
		'setUserInfo' | 'sendOperationalEvent'
	>;
	atlassianAccountId: string;
}): Promise<void> => {
	await pendingWrite;
	let resolution: QueuedResolution | undefined;
	try {
		resolution = readQueuedResolution();
		if (resolution && resolution.atlassianAccountId !== atlassianAccountId) {
			return;
		}
		AtlBrowserStorageSession.removeItem(WORKSPACE_RESOLUTION_ANALYTICS_STORAGE_KEY);
	} catch {
		return;
	}
	if (!resolution) {
		return;
	}
	try {
		analyticsClient.setUserInfo('atlassianAccount', atlassianAccountId);
		await analyticsClient.sendOperationalEvent({
			action: 'resolved',
			actionSubject: 'studioWorkspaceResolution',
			source: 'studioFoundationsRouting',
			attributes: {
				source: resolution.source,
				workspaceCountBucket: resolution.workspaceCountBucket,
			},
		});
	} catch {}
};

interface RootWorkspaceResolutionTrackerOptions {
	atlassianAccountId: string;
	context: WorkspaceContext | undefined;
	enabled: boolean;
	error: unknown;
	isFetching: boolean;
}

export const useRootWorkspaceResolutionTracker = ({
	atlassianAccountId,
	enabled,
	isFetching,
	context,
	error,
}: RootWorkspaceResolutionTrackerOptions): void => {
	di(trackRootWorkspaceResolution);
	const trackedAccounts = useRef(new Set<string>());

	useLayoutEffect(() => {
		const source = error ? 'error' : context?.resolutionSource;
		if (!enabled || isFetching || !source || trackedAccounts.current.has(atlassianAccountId)) {
			return;
		}
		trackedAccounts.current.add(atlassianAccountId);
		trackRootWorkspaceResolution(
			atlassianAccountId,
			source,
			error ? undefined : context?.workspaceCount,
		);
	}, [atlassianAccountId, enabled, isFetching, context, error]);
};
